import { type GridSortModel } from '@mui/x-data-grid-pro';
import { z, type ZodType } from 'zod';

import { initStorageAPI } from './initStorageApi';

type GridSortItem = GridSortModel[number];

const localStorageKeys = {
  TERMINCHECK_START_DATE: 'appointmentCheckStartDate',
  TERMINCHECK_END_DATE: 'appointmentCheckEndDate',
  THEMA_VA_GRID_FILTERMODEL: '/thema:filter',
  THEMA_VA_GRID_SORTMODEL: '/thema:sort',
  RESOURCE_CALENDAR_WEEK_COUNT: 'ResourceCalendar:weekCount',
  FEHLER_PAGE_SEARCH_PARAMS: '/fehler:params',
} as const;
type LocalStorageKey = (typeof localStorageKeys)[keyof typeof localStorageKeys];

const termincheckStartDateItemSchema = z.object({
  key: z.literal(localStorageKeys.TERMINCHECK_START_DATE),
  value: z.coerce.date(),
});
const termincheckEndDateItemSchema = z.object({
  key: z.literal(localStorageKeys.TERMINCHECK_END_DATE),
  value: z.coerce.date(),
});

const gridFilterModelExtendedItemSchema = z.object({
  key: z.literal(localStorageKeys.THEMA_VA_GRID_FILTERMODEL),
  value: z.object({
    items: z.array(
      z.object({
        field: z.string(),
        operator: z.string(),
        value: z.unknown().optional(),
        id: z.union([z.string(), z.number()]).optional(),
      }),
    ),
    logicOperator: z.any().optional(),
    quickFilterValues: z.array(z.unknown()).optional(),
    quickFilterLogicOperator: z.any().optional(),
    quickFilterExcludeHiddenColumns: z.boolean().optional(),
  }),
});

const gridSortModelItemSchema = z.object({
  key: z.literal(localStorageKeys.THEMA_VA_GRID_SORTMODEL),
  value: z
    .array(
      z.object({
        field: z.string(),
        sort: z
          .enum(['asc', 'desc'])
          .nullish()
          .transform((val) => val ?? null),
      } satisfies { [K in keyof GridSortItem]: ZodType<GridSortItem[K]> }),
    )
    .readonly(),
});

const resourceCalendarWeekCountItemSchema = z.object({
  key: z.literal(localStorageKeys.RESOURCE_CALENDAR_WEEK_COUNT),
  value: z.int().min(1),
});

const fehlerPageSearchParams = z.object({
  key: z.literal(localStorageKeys.FEHLER_PAGE_SEARCH_PARAMS),
  value: z.string(),
});

const localStorageApi = initStorageAPI(localStorage, {
  [localStorageKeys.TERMINCHECK_START_DATE]: termincheckStartDateItemSchema,
  [localStorageKeys.TERMINCHECK_END_DATE]: termincheckEndDateItemSchema,
  [localStorageKeys.THEMA_VA_GRID_FILTERMODEL]: gridFilterModelExtendedItemSchema,
  [localStorageKeys.THEMA_VA_GRID_SORTMODEL]: gridSortModelItemSchema,
  [localStorageKeys.RESOURCE_CALENDAR_WEEK_COUNT]: resourceCalendarWeekCountItemSchema,
  [localStorageKeys.FEHLER_PAGE_SEARCH_PARAMS]: fehlerPageSearchParams,
} satisfies Record<LocalStorageKey, ZodType>);

export { localStorageApi, localStorageKeys };
